import React, { useState, useEffect, useRef } from 'react';

interface ProfileImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  /** Render the initials fallback as a neutral cream avatar (no colored gradient). */
  neutral?: boolean;
}

/**
 * ProfileImage Component with Smart Avatar Fallbacks
 * 
 * Features:
 * - Displays user profile image when available
 * - Falls back to beautiful gradient avatar with user initials
 * - 12 different gradient color combinations for variety
 * - Responsive font sizing based on container size
 * - Consistent color assignment per user (same user = same color)
 * - Supports single name (first initial) or full name (first + last initial)
 * - Loading states and error handling
 */

// Beautiful color palette for profile avatars
const AVATAR_COLORS = [
  'bg-gradient-to-br from-purple-500 to-pink-500',
  'bg-gradient-to-br from-blue-500 to-cyan-500',
  'bg-gradient-to-br from-green-500 to-teal-500',
  'bg-gradient-to-br from-orange-500 to-red-500',
  'bg-gradient-to-br from-indigo-500 to-purple-500',
  'bg-gradient-to-br from-pink-500 to-rose-500',
  'bg-gradient-to-br from-cyan-500 to-blue-500',
  'bg-gradient-to-br from-teal-500 to-green-500',
  'bg-gradient-to-br from-amber-500 to-orange-500',
  'bg-gradient-to-br from-violet-500 to-purple-500',
  'bg-gradient-to-br from-emerald-500 to-teal-500',
  'bg-gradient-to-br from-sky-500 to-blue-500',
];

// Generate a consistent color based on a name
const getAvatarColor = (name: string): string => {
  if (!name) return AVATAR_COLORS[0];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

// Get initials from name
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const names = name.trim().split(/\s+/);
  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  }
  
  // For multiple names, take first letter of first and last name
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

export const ProfileImage: React.FC<ProfileImageProps> = ({ src, alt, className = '', neutral = false }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldLoad, setShouldLoad] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !src) return;

    // Check if element is already in viewport
    const rect = container.getBoundingClientRect();
    const isInViewport = (
      rect.top >= -50 &&
      rect.left >= -50 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 50 &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth) + 50
    );

    if (isInViewport) {
      // Already in viewport, load immediately
      setShouldLoad(true);
      return;
    }

    // Not in viewport, set up observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the image enters viewport
        threshold: 0.01,
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [src]);

  // Reset states when src changes and set timeout
  useEffect(() => {
    setHasError(false);
    setIsLoading(!!src); // Only set loading if there's a src
    setShouldLoad(false); // Reset shouldLoad when src changes
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set a timeout to stop loading after 10 seconds if image hasn't loaded
    if (src && shouldLoad) {
      timeoutRef.current = setTimeout(() => {
        console.warn('Profile image timed out after 10 seconds:', src);
        setHasError(true);
        setIsLoading(false);
      }, 10000); // 10 second timeout (increased from 5)
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [src, shouldLoad]);

  // Generate avatar placeholder
  const generateAvatar = () => {
    const initials = getInitials(alt);
    const colorClass = neutral ? 'bg-[#EFE3C4]' : getAvatarColor(alt);
    
    // Determine font size based on container size (responsive)
    const getFontSize = () => {
      // Check for explicit size classes
      if (className.includes('w-4') || className.includes('h-4')) return 'text-xs';
      if (className.includes('w-6') || className.includes('h-6')) return 'text-xs';
      if (className.includes('w-8') || className.includes('h-8')) return 'text-sm';
      if (className.includes('w-10') || className.includes('h-10')) return 'text-sm';
      if (className.includes('w-12') || className.includes('h-12')) return 'text-base';
      if (className.includes('w-16') || className.includes('h-16')) return 'text-lg';
      if (className.includes('w-20') || className.includes('h-20')) return 'text-xl';
      
      // Check for common flex classes that might indicate larger containers
      if (className.includes('w-full') || className.includes('h-full')) {
        // These are usually in containers, so use medium size
        return 'text-base';
      }
      
      return 'text-sm'; // default
    };
    
    return (
      <div className={`flex items-center justify-center ${colorClass} ${className} rounded-full shadow-lg`}>
        <span className={`${neutral ? 'text-[#3D2B1F]' : 'text-white'} font-bold ${getFontSize()} select-none tracking-wide`}>
          {initials}
        </span>
      </div>
    );
  };

  if (!src || hasError) {
    return generateAvatar();
  }

  // Show avatar while waiting for intersection observer to trigger
  if (!shouldLoad) {
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        {generateAvatar()}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className} rounded-full overflow-hidden`}>
      {/* Show avatar as background while image loads */}
      {isLoading && (
        <div className="absolute inset-0">
          {generateAvatar()}
        </div>
      )}
      
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full h-full object-cover rounded-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => {
          // Clear timeout since image loaded successfully
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setIsLoading(false);
        }}
        onError={(e) => {
          console.error('Profile image failed to load:', src, e);
          // Clear timeout since we got an error
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setHasError(true);
          setIsLoading(false);
        }}
      />
    </div>
  );
}; 